'use client';

import { useEffect, useState } from 'react';
import { PortableText } from '@portabletext/react';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';
import Image from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { Motion } from '@/components/global/utils/Motion';
import { TrustpilotMicroTrustScore } from '@/components/global/TrustpilotWidgets';
import { resolveLink } from '@/sanity/globals/CTA';
import { DEFAULT_LANGUAGE } from '@/sanity/globals/I18n';
import { PageTextOnlyCryptoSchema } from '@/sanity/pages/PageTextOnlyCryptoTemplate';

import heroBg from '@/public/text-only-crypto/hero-bg.jpg';
import logo from '@/public/logo-black.svg';

/**
 * Figma: desktop 7205:2408, mobile 8662:24424. The logo bar belongs to this
 * frame, not to page chrome.
 */
export default function HeroSection({
  data,
  className,
  showTopBanner,
}: {
  data: PageTextOnlyCryptoSchema;
  className?: string;
  showTopBanner?: boolean;
}) {
  const lang = data?.settings?.language || DEFAULT_LANGUAGE;
  const isGerman = stegaClean(lang) === 'de';
  const homeHref = resolveLink('/', lang);
  const [finalLink, setFinalLink] = useState('');

  useEffect(() => {
    if (!homeHref) return;
    setFinalLink(homeHref);
  }, [homeHref]);

  const reveal = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.3, delay, ease: [0.25, 0.46, 0.45, 0.94] },
  });

  return (
    <section
      id="hero-section"
      className={cn(
        'relative flex flex-col items-center w-full overflow-clip bg-white max-lg:pb-[8rem] lg:pb-[19.656rem]',
        showTopBanner && 'mt-[16rem] lg:mt-[7rem]',
        className,
      )}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 -translate-x-1/2 max-lg:top-[-2.75rem] max-lg:w-[149.5rem] max-lg:h-[83.5rem] lg:top-[-5.9375rem] lg:w-screen lg:min-w-[300rem] lg:h-[162.5rem]">
          <Image
            className="absolute inset-0 max-w-none object-fill opacity-[0.28]"
            style={{
              width: '100%',
              height: '100%',
              filter: 'grayscale(1) brightness(1.6)',
            }}
            src={heroBg}
            quality={100}
            priority
            alt=""
          />
        </div>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(to bottom, #fff 5.41%, rgba(255,255,255,0) 122.91%), linear-gradient(to top, #fff 0.69%, rgba(255,255,255,0) 51.96%)',
          }}
        />
      </div>

      <nav className="relative z-[2] flex items-center justify-center w-full h-[8.375rem] bg-white border-b border-black/[0.15] pt-[1.375rem] pb-[1rem]">
        <Motion
          className="flex items-center justify-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <a href={finalLink}>
            <Image
              alt="Logo"
              src={logo}
              className="w-[16.988rem] h-[3.146rem]"
            />
          </a>
        </Motion>
      </nav>

      <div className="relative z-[1] flex flex-col items-center w-full max-lg:px-[2rem] max-lg:mt-[8rem] lg:mt-[19.75rem] max-lg:gap-[3rem] lg:gap-[3.5rem]">
        <div className="flex flex-col items-center gap-[1.25rem]">
          {data?.heroChip && (
            <Motion
              className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black text-center"
              type="p"
              {...reveal()}
            >
              {data.heroChip}
            </Motion>
          )}

          {data?.heroTitle && (
            <Motion
              className="richtext text-center
              prose-h1:font-geist max-lg:prose-h1:text-[5rem] lg:prose-h1:text-[7rem] prose-h1:font-semibold prose-h1:leading-none max-lg:prose-h1:tracking-[-0.25rem] lg:prose-h1:tracking-[-0.35rem] prose-h1:text-black max-lg:prose-h1:mb-[2.25rem] lg:prose-h1:mb-[3.5rem] max-lg:prose-h1:max-w-[47.125rem] lg:prose-h1:w-[89.5rem]
              [&_h1_span]:font-normal [&_h1_span]:italic [&_h1_span]:leading-[0] max-lg:[&_h1>span]:![background:linear-gradient(to_top,#E2F3EF_79%,transparent_79%)] lg:[&_h1>span]:![background:linear-gradient(to_top,#E2F3EF_57%,transparent_57%)] [&_h1_span_span]:!bg-none
              prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/70 prose-p:mb-0 max-lg:prose-p:max-w-[47.125rem] lg:prose-p:w-[66.125rem]"
              {...reveal(0.05)}
            >
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </Motion>
          )}
        </div>

        <Motion
          className="flex flex-col items-center justify-center max-lg:w-full gap-[1.5rem]"
          {...reveal(0.1)}
        >
          {data?.heroCTA && (
            <CTAButton
              data={data.heroCTA}
              overrideVariant="default"
              className="max-sm:!w-full sm:!w-auto justify-center
              max-sm:[&_.button]:!w-full sm:[&_.button]:!w-[29.75rem] [&_.button]:!min-h-[5.75rem] [&_.button]:!rounded-[0.25rem]
              [&_.button]:!px-[3.5rem] [&_.button]:!text-[1.75rem] [&_.button]:!tracking-[-0.035rem]"
            />
          )}

          <div className={isGerman ? 'w-[44rem] lg:w-[60rem]' : 'w-[44rem] lg:w-[50rem]'}>
            <TrustpilotMicroTrustScore
              locale={isGerman ? 'de-DE' : 'en-US'}
              theme="light"
            />
          </div>
        </Motion>
      </div>
    </section>
  );
}
